import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useStickerSectionStore } from "../MugEditorFeature/EditorStore";
import { CanvasDrawer } from "../MugEditorFeature/CanvasDrawer";
import { ImageStore } from "../MugEditorFeature/ImageStore";
import localforage from "localforage";
import { BACKEND_URL } from "../Config";
import LoadingScreen from "../MugEditorFeature/EditorComponents/LoadingScreen";
import { androidPhones, iOSPhones, phoneTemplateImages } from "../MugEditorFeature/utils/phoneDetails";
import { trybeGetVisitorId } from "../MugEditorFeature/utils/trybeTracking";

export default function OrderSummary() {
    const navigate = useNavigate();
    const phoneModel = useStickerSectionStore((state) => state.phoneModel);
    const caseFinish = useStickerSectionStore((state) => state.caseFinish);
    const setCaseFinish = useStickerSectionStore((state) => state.setCaseFinish);
    const magSafe = useStickerSectionStore((state) => state.magSafe);
    const setMagSafe = useStickerSectionStore((state) => state.setMagSafe);
    const backgroundColor = useStickerSectionStore((state) => state.backgroundColor);
    const showBackgroundImage = useStickerSectionStore((state) => state.showBackgroundImage);
    const ghostOpacity = useStickerSectionStore((state) => state.ghostOpacity);
    const [loading, setLoading] = useState(false);
    const [templateUrl, setTemplateUrl] = useState<string | null>(null);

    const supportsMagSafe = phoneModel
        ? (phoneTemplateImages[phoneModel]?.glossyMagSafe !== undefined &&
            phoneTemplateImages[phoneModel]?.matteMagSafe !== undefined &&
            phoneTemplateImages[phoneModel]?.glossyMagSafe !== "MAGSAFE_ID" &&
            phoneTemplateImages[phoneModel]?.matteMagSafe !== "MAGSAFE_ID")
        : false;

    const getPhoneName = (model: string | null): string => {
        if (!model) return '';
        const allPhones = [...iOSPhones, ...androidPhones];
        const phone = allPhones.find(p => p.value === model);
        return phone ? phone.name : model.replace(/_/g, ' ').toUpperCase();
    };

    const setFinish = async (finish: string) => {
        setCaseFinish(finish);
        await localforage.setItem('caseFinish', finish);
    };

    const setMagSafeOption = async (enabled: boolean) => {
        setMagSafe(enabled);
        await localforage.setItem('magSafe', enabled);
    };

    useEffect(() => {
        const generatePreview = async () => {
            if (!phoneModel) return;
            const url = await CanvasDrawer.captureCanvas(null);
            if (url) {
                setTemplateUrl(url);
            }
        };
        generatePreview();
    }, [phoneModel]);

    // Converts the user's chosen background image (already downsampled to
    // <=2048px by BackgroundEditor.tsx when it was uploaded) to a PNG blob,
    // the same way sticker cutouts are already turned into blobs elsewhere.
    const bitmapToPngBlob = (bitmap: ImageBitmap): Promise<Blob | null> => {
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            canvas.width = bitmap.width;
            canvas.height = bitmap.height;
            const ctx = canvas.getContext('2d');
            if (!ctx) return resolve(null);
            ctx.drawImage(bitmap, 0, 0);
            canvas.toBlob((blob) => resolve(blob), 'image/png');
        });
    };

    // Uploads everything a laptop-side reconstruction pass needs to rebuild
    // the full template later with a higher-quality background remover:
    // each sticker's raw (pre-removal) photo, its placement (position/
    // rotation/flip), the realized ghost-layer instances (captured as-is so
    // a later rebuild can stay visually consistent instead of re-rolling
    // the randomness), and the background/case settings captureCanvas()
    // itself reads. Best-effort: a failure here shouldn't block checkout,
    // since the flattened template already covers the actual print file.
    const uploadCollageRawImages = async (orderUuid: string) => {
        try {
            const placements = CanvasDrawer.canvasImages.map(ci => ci.getInstanceDict());
            if (placements.length === 0) return;

            const distinctImageIds = Array.from(new Set(placements.map(p => p.imageID)));
            const ghosts = CanvasDrawer.getFinalGhostInstances();

            const formData = new FormData();
            formData.append('uuid', orderUuid);
            formData.append('placements', JSON.stringify(placements));
            formData.append('ghosts', JSON.stringify(ghosts));
            formData.append('phoneModel', phoneModel ?? '');
            formData.append('backgroundColor', backgroundColor);
            formData.append('showBackgroundImage', showBackgroundImage.toString());
            formData.append('ghostOpacity', ghostOpacity.toString());

            if (showBackgroundImage && CanvasDrawer.BackgroundImageBitmap) {
                const backgroundBlob = await bitmapToPngBlob(CanvasDrawer.BackgroundImageBitmap);
                if (backgroundBlob) {
                    formData.append('background', backgroundBlob, 'background.png');
                }
            }

            let fileCount = 0;
            for (const imageId of distinctImageIds) {
                const rawBlob = await ImageStore.getRawImageBlob(imageId);
                if (rawBlob) {
                    formData.append(imageId, rawBlob, `${imageId}.png`);
                    fileCount += 1;
                }
            }

            if (fileCount === 0) return;

            await fetch(BACKEND_URL + '/upload_collage_images', {
                method: "POST",
                credentials: "include",
                body: formData
            });
        } catch (error) {
            console.error("Failed to upload raw collage images:", error);
        }
    };

    const handleCheckout = async () => {
        if (!phoneModel) return;

        setLoading(true);

        try {
            const phoneData = phoneTemplateImages[phoneModel];
            let variantID: string;

            if (magSafe && phoneData.glossyMagSafe && phoneData.matteMagSafe) {
                // MagSafe version
                variantID = caseFinish === "Matte" ? phoneData.matteMagSafe : phoneData.glossyMagSafe;
            } else {
                // Regular version
                variantID = caseFinish === "Matte" ? phoneData.matte : phoneData.glossy;
            }

            const url = await CanvasDrawer.captureCanvas(null);
            if (!url) {
                alert("Failed to capture design. Please try again.");
                setLoading(false);
                return;
            }

            const blob = await fetch(url).then(res => res.blob());
            if (!blob) {
                alert("Failed to process design. Please try again.");
                setLoading(false);
                return;
            }

            const formData = new FormData();
            formData.append('file', blob, 'template.png');
            formData.append('variantID', variantID);
            formData.append('magSafe', magSafe.toString());
            const vid = trybeGetVisitorId();
            if (vid) formData.append('vid', vid);

            const stripeResponse = await fetch(BACKEND_URL + '/create_checkout_session', {
                method: "POST",
                credentials: "include",
                body: formData
            });

            const stripeResult = await stripeResponse.json();

            if (stripeResponse.ok && stripeResult.checkout_url) {
                if (stripeResult.file_uuid) {
                    await uploadCollageRawImages(stripeResult.file_uuid);
                }
                window.location.href = stripeResult.checkout_url;
            } else {
                console.error("Stripe session creation failed:", stripeResult);
                alert("Failed to create checkout session. Please try again.");
                setLoading(false);
            }
        } catch (error) {
            console.error("Checkout error:", error);
            alert("An error occurred during checkout. Please try again.");
            setLoading(false);
        }
    };

    if (!phoneModel) {
        navigate('/caseconfiguration');
        return null;
    }

    if (loading) {
        return (
            <div style={{
                width: "100vw",
                height: "100vh",
                backgroundColor: "#7a9a9a",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#f4f8f8"
            }}>
                <LoadingScreen />
            </div>
        );
    }

    return (
        <div style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "100vw",
            height: "100vh",
            backgroundColor: "#7a9a9a",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#f4f8f8",
            padding: "10px",
            boxSizing: "border-box",
            overflow: "hidden"
        }}>
            <div style={{
                maxWidth: "450px",
                width: "100%",
                height: "95%",
                backgroundColor: "#4a6a6a",
                borderRadius: "15px",
                boxSizing: "border-box",
                border: "1px solid #3d5a5a",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden"
            }}>
                <div style={{
                    padding: "20px",
                    borderBottom: "1px solid #3d5a5a",
                    backgroundColor: "#3d5a5a"
                }}>
                    <h1 style={{
                        fontSize: "24px",
                        margin: 0,
                        textAlign: "center",
                        color: "#d4e8e8",
                        fontFamily: "'Quicksand', sans-serif"
                    }}>Order Summary</h1>
                </div>

                <div style={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    padding: "15px",
                    boxSizing: "border-box",
                    WebkitOverflowScrolling: "touch"
                } as React.CSSProperties}>
                    {templateUrl && (
                        <div style={{
                            marginBottom: "15px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: "10px",
                            backgroundColor: "#5a8080",
                            borderRadius: "8px",
                            border: "1px solid #6b9e9e"
                        }}>
                            <img
                                src={templateUrl}
                                alt="Case Preview"
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "200px",
                                    borderRadius: "6px",
                                    objectFit: "contain"
                                }}
                            />
                        </div>
                    )}

                    <div style={{
                        marginBottom: "15px",
                        padding: "12px 15px",
                        backgroundColor: "#5a8080",
                        borderRadius: "8px",
                        border: "1px solid #6b9e9e"
                    }}>
                        <h3 style={{ fontSize: "13px", marginBottom: "6px", color: "#a8d8d8", fontWeight: "500", margin: 0 }}>Phone Model</h3>
                        <p style={{ fontSize: "17px", fontWeight: "600", color: "#f4f8f8", margin: "6px 0 0 0" }}>
                            {getPhoneName(phoneModel)}
                        </p>
                    </div>

                    {supportsMagSafe && (
                        <div style={{ marginBottom: "15px" }}>
                            <h3 style={{ fontSize: "13px", marginBottom: "8px", color: "#a8d8d8", fontWeight: "500" }}>MagSafe Compatible</h3>

                            <div
                                onClick={() => setMagSafeOption(!magSafe)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    padding: "10px 12px",
                                    backgroundColor: magSafe ? "#6b9e9e" : "#5a7a7a",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    border: magSafe ? "2px solid #8bb8b8" : "2px solid #4a6a6a",
                                    transition: "all 0.2s"
                                }}
                            >
                                <div style={{
                                    width: "18px",
                                    height: "18px",
                                    borderRadius: "3px",
                                    border: "2px solid #f4f8f8",
                                    marginRight: "10px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: magSafe ? "#8bb8b8" : "transparent",
                                    flexShrink: 0
                                }}>
                                    {magSafe && (
                                        <div style={{ color: "#3d5a5a", fontWeight: "bold", fontSize: "12px" }}>✓</div>
                                    )}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: "15px", fontWeight: "500", color: "#f4f8f8" }}>Add MagSafe</div>
                                    <div style={{ fontSize: "12px", color: "#d4e8e8", marginTop: "2px" }}>
                                        Built-in magnets
                                    </div>
                                </div>
                                <div style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: magSafe ? "#a8f0f0" : "#6b9e9e",
                                    marginLeft: "8px",
                                    textShadow: magSafe ? "0 0 8px rgba(168, 240, 240, 0.6)" : "none",
                                    transition: "all 0.2s"
                                }}>
                                    +$5
                                </div>
                            </div>
                        </div>
                    )}

                    <div>
                        <h3 style={{ fontSize: "13px", marginBottom: "8px", color: "#a8d8d8", fontWeight: "500" }}>Case Finish</h3>

                        <div
                            onClick={() => setFinish("Glossy")}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "10px 12px",
                                marginBottom: "8px",
                                backgroundColor: caseFinish === "Glossy" ? "#6b9e9e" : "#5a7a7a",
                                borderRadius: "8px",
                                cursor: "pointer",
                                border: caseFinish === "Glossy" ? "2px solid #8bb8b8" : "2px solid #4a6a6a",
                                transition: "all 0.2s"
                            }}
                        >
                            <div style={{
                                width: "18px",
                                height: "18px",
                                borderRadius: "50%",
                                border: "2px solid #f4f8f8",
                                marginRight: "10px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0
                            }}>
                                {caseFinish === "Glossy" && (
                                    <div style={{
                                        width: "8px",
                                        height: "8px",
                                        borderRadius: "50%",
                                        backgroundColor: "#8bb8b8"
                                    }} />
                                )}
                            </div>
                            <div>
                                <div style={{ fontSize: "15px", fontWeight: "500", color: "#f4f8f8" }}>Glossy</div>
                                <div style={{ fontSize: "12px", color: "#d4e8e8", marginTop: "2px" }}>
                                    Shiny, reflective
                                </div>
                            </div>
                        </div>

                        <div
                            onClick={() => setFinish("Matte")}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "10px 12px",
                                backgroundColor: caseFinish === "Matte" ? "#6b9e9e" : "#5a7a7a",
                                borderRadius: "8px",
                                cursor: "pointer",
                                border: caseFinish === "Matte" ? "2px solid #8bb8b8" : "2px solid #4a6a6a",
                                transition: "all 0.2s"
                            }}
                        >
                            <div style={{
                                width: "18px",
                                height: "18px",
                                borderRadius: "50%",
                                border: "2px solid #f4f8f8",
                                marginRight: "10px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0
                            }}>
                                {caseFinish === "Matte" && (
                                    <div style={{
                                        width: "8px",
                                        height: "8px",
                                        borderRadius: "50%",
                                        backgroundColor: "#8bb8b8"
                                    }} />
                                )}
                            </div>
                            <div>
                                <div style={{ fontSize: "15px", fontWeight: "500", color: "#f4f8f8" }}>Matte</div>
                                <div style={{ fontSize: "12px", color: "#d4e8e8", marginTop: "2px" }}>
                                    Smooth, non-reflective
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <div style={{
                    borderTop: "1px solid #3d5a5a",
                    padding: "15px",
                    backgroundColor: "#3d5a5a"
                }}>
                    <div style={{
                        padding: "10px 12px",
                        backgroundColor: "#5a8080",
                        borderRadius: "8px",
                        marginBottom: "12px",
                        border: "1px solid #6b9e9e",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                    }}>
                        <div style={{ fontSize: "15px", fontWeight: "500", color: "#f4f8f8" }}>Total</div>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                            <div style={{ fontSize: "18px", fontWeight: "700", color: "#d4e8e8" }}>
                                ${magSafe && supportsMagSafe ? "39.99" : "34.99"}
                            </div>
                            <div style={{ fontSize: "11px", color: "#a8d8d8", marginTop: "2px" }}>
                                + tax
                            </div>
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: "10px" }}>
                        <button
                            onClick={() => navigate('/editor')}
                            style={{
                                flex: 1,
                                padding: "11px",
                                fontSize: "14px",
                                borderRadius: "8px",
                                border: "2px solid #f4f8f8",
                                backgroundColor: "transparent",
                                color: "#f4f8f8",
                                cursor: "pointer",
                                fontFamily: "'Quicksand', sans-serif",
                                fontWeight: "500"
                            }}
                        >
                            Back
                        </button>
                        <button
                            onClick={handleCheckout}
                            style={{
                                flex: 1,
                                padding: "11px",
                                fontSize: "14px",
                                fontWeight: "600",
                                borderRadius: "8px",
                                border: "2px solid #3d5a5a",
                                backgroundColor: "#6b9e9e",
                                color: "#f4f8f8",
                                cursor: "pointer",
                                boxShadow: "0 0 10px 2px rgba(107, 158, 158, 0.5)",
                                fontFamily: "'Quicksand', sans-serif"
                            }}
                        >
                            Checkout
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
